import React, { useState } from 'react';
import { FiUser, FiPhone, FiCreditCard } from 'react-icons/fi';
import API, { getSchemaName, getImagenUrl } from 'shared/api';

function RegistroCliente({
  sessionId,
  schemaName = getSchemaName(),
  brandName = 'Agorax',
  logoUrl,
  onCompletado,
}) {
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [telefono, setTelefono] = useState('');
  const [cedula, setCedula] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const handleTelefonoChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val.length <= 15) setTelefono(val);
  };

  const handleCedulaChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val.length <= 12) setCedula(val);
  };

  const validar = () => {
    if (!nombre.trim()) return 'Ingresa tu nombre';
    if (!apellido.trim()) return 'Ingresa tu apellido';
    if (!telefono.trim() || telefono.length < 10)
      return 'Ingresa un teléfono válido (al menos 10 dígitos)';
    if (!cedula.trim() || cedula.length < 5)
      return 'Ingresa una cédula válida (al menos 5 dígitos)';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validar();
    if (err) {
      setError(err);
      return;
    }

    setEnviando(true);
    setError(null);
    try {
      const res = await API.post('/registro/registrar', {
        session_id: sessionId,
        schema_name: schemaName,
        nombre: nombre.trim(),
        apellido: apellido.trim(),
        telefono: telefono.trim(),
        cedula: cedula.trim(),
      });
      if (res.data.status === 'success') {
        localStorage.setItem(`agorax_registro_${schemaName}`, 'true');
        onCompletado(res.data.data);
      } else {
        setError(res.data.message || 'Error al registrar');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || '';
      setError(msg || 'Error de conexión. Intenta de nuevo.');
      console.error('Error en registro:', err.response?.status, msg || err.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={S.wrapper}>
      <div style={S.card}>
        <img
          src={
            logoUrl
              ? getImagenUrl(logoUrl)
              : '/logo-agorax.jpeg'
          }
          alt={brandName}
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '12px',
            objectFit: 'contain',
            marginBottom: '12px',
          }}
        />
        <h1 style={S.title}>{brandName.toUpperCase()}</h1>
        <p style={S.subtitle}>Registrate para comenzar</p>

        {error && <div style={S.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={S.form}>
          <div style={S.inputGroup}>
            <FiUser size={18} color="var(--color-text-secondary)" />
            <input
              placeholder="Nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              style={S.input}
              autoComplete="given-name"
            />
          </div>
          <div style={S.inputGroup}>
            <FiUser size={18} color="var(--color-text-secondary)" />
            <input
              placeholder="Apellido"
              value={apellido}
              onChange={(e) => setApellido(e.target.value)}
              style={S.input}
              autoComplete="family-name"
            />
          </div>
          <div style={S.inputGroup}>
            <FiPhone size={18} color="var(--color-text-secondary)" />
            <input
              placeholder="Teléfono (ej: 04121234567)"
              value={telefono}
              onChange={handleTelefonoChange}
              style={S.input}
              type="tel"
              autoComplete="tel"
            />
          </div>
          <div style={S.inputGroup}>
            <FiCreditCard size={18} color="var(--color-text-secondary)" />
            <input
              placeholder="Cédula (ej: 12345678)"
              value={cedula}
              onChange={handleCedulaChange}
              style={S.input}
              autoComplete="off"
            />
          </div>

          <button
            type="submit"
            disabled={enviando}
            style={{ ...S.btn, opacity: enviando ? 0.7 : 1 }}
          >
            {enviando ? 'Registrando...' : 'Comenzar'}
          </button>
        </form>
      </div>
    </div>
  );
}

const S = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '24px',
    backgroundColor: 'var(--color-bg)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '24px',
    padding: '36px 24px',
    width: '100%',
    maxWidth: '380px',
    boxShadow: '0 4px 24px rgba(11,26,74,0.08)',
    textAlign: 'center',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    color: 'var(--color-primary)',
    margin: '0 0 4px',
    letterSpacing: '-0.3px',
  },
  subtitle: { fontSize: '14px', color: 'var(--color-text-secondary)', margin: '0 0 24px' },
  error: {
    fontSize: '13px',
    color: 'var(--color-danger)',
    backgroundColor: 'var(--color-danger-light)',
    padding: '10px 14px',
    borderRadius: '12px',
    marginBottom: '16px',
    textAlign: 'left',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: 'var(--color-bg)',
    borderRadius: '12px',
    padding: '12px 14px',
    border: '1px solid var(--color-border)',
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: '15px',
    fontFamily: 'inherit',
    color: 'var(--color-primary)',
  },
  btn: {
    padding: '14px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: 'var(--color-secondary)',
    color: 'var(--color-surface)',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'opacity 0.2s',
    fontFamily: 'inherit',
  },
};

export default RegistroCliente;
